import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '@/lib/i18n';

/**
 * 현재 i18n 언어 설정에 따라 영문/한국어 텍스트를 반환하는 유틸.
 * ko가 없으면 en을 fallback으로 사용한다.
 * (비-React 컨텍스트에서도 사용 가능 - 단, 언어 변경 시 자동 리렌더링 없음)
 *
 * @example
 * getText('Campaigns', '캠페인')   // 한국어 모드 → '캠페인'
 * getText('Dashboard')             // ko 미지정 → 항상 'Dashboard'
 */
export function getText(en: string, ko?: string): string {
  if (getCurrentLanguage() === 'ko' && ko != null) return ko;
  return en;
}

/**
 * JSX/ReactNode 버전의 getText.
 * 태그가 포함된 복잡한 다국어 콘텐츠를 처리할 때 사용한다.
 * (비-React 컨텍스트에서도 사용 가능 - 단, 언어 변경 시 자동 리렌더링 없음)
 *
 * @example
 * getJSX(<>Welcome <strong>Admin</strong></>, <>관리자님 <strong>환영</strong>합니다</>)
 */
export function getJSX(en: ReactNode, ko?: ReactNode): ReactNode {
  if (getCurrentLanguage() === 'ko' && ko != null) return ko;
  return en;
}

/**
 * React 컴포넌트 내에서 사용하는 훅 버전.
 * i18n 언어 변경 시 자동으로 리렌더링을 트리거한다.
 *
 * @example
 * const { t, tj } = useI18nText();
 * return <h1>{t('Dashboard', '대시보드')}</h1>;
 */
export function useI18nText() {
  const { i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ko';

  const t = useCallback(
    (en: string, ko?: string): string => {
      if (lang === 'ko' && ko != null) return ko;
      return en;
    },
    [lang],
  );

  const tj = useCallback(
    (en: ReactNode, ko?: ReactNode): ReactNode => {
      if (lang === 'ko' && ko != null) return ko;
      return en;
    },
    [lang],
  );

  return { t, tj, lang } as const;
}
